"use client";

import React, { useMemo } from "react";
import ReactHtmlParser from "@/src/utils/html-parser";
import { Box } from "@mui/material";
import Kicker from "@/src/components/common/broadsheet/kicker";
import { FONT_UI } from "@/src/components/common/broadsheet/tokens";
import type Outline from "@/src/_types/outline";

const outlinesDistinguishGuide: string[] = ["h2", "h3", "h4", "h5", "h6"];

const ABSTRACT_ANCHOR = "page-summary";
const BODY_ANCHOR = "page-content-body";
const stripTags = (label: string) =>
  label.replace(/<\/?[^>]+(>|$)/g, "").trim();

type OutlineEntry = Outline & { level: number };

const extractOutlines = (content: string): OutlineEntry[] => {
  const extracted: OutlineEntry[] = [];
  const seen = new Set<string>();

  const walk = (node: any) => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (!node || typeof node !== "object" || !("type" in node)) return;

    const props = node.props;

    if (outlinesDistinguishGuide.includes(String(node.type)) && props?.id) {
      const label = stripTags(String(props.label ?? ""));
      if (label && !seen.has(props.id)) {
        seen.add(props.id);
        const level = Number(String(node.type).slice(1)) || 2;
        extracted.push({ id: props.id, label, level } as OutlineEntry);
      }
      return;
    }

    if (props?.children) walk(props.children);
  };

  walk(ReactHtmlParser(content));

  return extracted;
};

const Outlines = ({
  content,
  hasAbstract,
  hasBody,
  items,
  label = "Outline",
}: {
  content?: string;
  hasAbstract?: boolean;
  hasBody?: boolean;
  items?: { id: string; label: string; level?: number }[];
  label?: string;
}) => {
  const outlines = useMemo(
    () =>
      items
        ? items.map((item) => ({ ...item, level: item.level ?? 2 }))
        : extractOutlines(content ?? ""),
    [content, items],
  );

  const handleScrollToSection = (
    event: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string,
  ) => {
    const section = document.getElementById(sectionId);
    if (section) {
      event.preventDefault();
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  const shallowest = outlines.length
    ? Math.min(...outlines.map((item) => item.level))
    : 2;

  const entries: OutlineEntry[] = [
    ...(hasAbstract
      ? [
          {
            id: ABSTRACT_ANCHOR,
            label: "Summary",
            level: shallowest,
          } as OutlineEntry,
        ]
      : []),
    ...(hasBody
      ? [{ id: BODY_ANCHOR, label: "Body", level: shallowest } as OutlineEntry]
      : []),
    ...outlines,
  ];

  if (entries.length === 0) return null;

  return (
    <Box component="nav" aria-label={label}>
      <Kicker sx={{ mb: 0.375 }}>{label}</Kicker>
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
        {entries.map((item, index) => (
          <Box component="li" key={`outline-${index}`} sx={{ m: 0 }}>
            <Box
              component="a"
              href={`#${item.id}`}
              onClick={(event: React.MouseEvent<HTMLAnchorElement>) =>
                handleScrollToSection(event, item.id)
              }
              sx={{
                display: "block",
                fontFamily: FONT_UI,
                fontSize: "0.65rem",
                lineHeight: 0.9,
                color: "custom.inkMuted",
                textDecoration: "none",
                borderLeft: "2px solid",
                borderColor: "custom.ruleHairline",
                pl: `${0.625 + Math.min(item.level - shallowest, 2) * 0.5}rem`,
                paddingBlock: "0.5rem",
                "&:hover": {
                  color: "custom.link",
                  borderColor: "custom.link",
                },
              }}
            >
              {item.label}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Outlines;
